import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// Threshold in pixels to account for rounding errors in scroll calculations
const SCROLL_THRESHOLD = 2;

type UseCardCarouselProps = {
	itemCount: number;
	initialIndex?: number;
};

type UseCardCarouselReturn = {
	scrollContainerRef: React.RefObject<HTMLDivElement | null>;
	itemRefs: React.MutableRefObject<Array<HTMLDivElement | null>>;
	activeIndex: number;
	canScrollLeft: boolean;
	canScrollRight: boolean;
	needsScroll: boolean;
	handleScrollLeft: () => void;
	handleScrollRight: () => void;
	setItemRef: (index: number) => (el: HTMLDivElement | null) => void;
};

type CarouselRefs = {
	scrollContainerRef: React.RefObject<HTMLDivElement | null>;
	itemRefs: React.MutableRefObject<Array<HTMLDivElement | null>>;
};

type UseCarouselActiveIndexOptions = {
	itemCount: number;
	initialIndex: number;
	loop: boolean;
	updateScrollState: () => void;
};

function findClosestItemIndex(
	container: HTMLDivElement,
	itemRefs: React.MutableRefObject<Array<HTMLDivElement | null>>,
): number {
	const containerCenter = container.scrollLeft + container.offsetWidth / 2;

	let closestIndex = 0;
	let smallestDistance = Infinity;

	itemRefs.current.forEach((el, index) => {
		if (!el) return;

		const itemCenter = el.offsetLeft + el.offsetWidth / 2;
		const distance = Math.abs(containerCenter - itemCenter);

		if (distance < smallestDistance) {
			smallestDistance = distance;
			closestIndex = index;
		}
	});

	return closestIndex;
}

function createScrollHandler(
	scrollTimeoutRef: React.MutableRefObject<number | null>,
	updateActiveIndex: () => void,
): () => void {
	return () => {
		const timeoutRef = scrollTimeoutRef;
		if (timeoutRef.current) {
			window.clearTimeout(timeoutRef.current);
		}

		timeoutRef.current = window.setTimeout(() => {
			updateActiveIndex();
		}, 100);
	};
}

function setupScrollListener(
	container: HTMLDivElement,
	scrollTimeoutRef: React.MutableRefObject<number | null>,
	updateActiveIndex: () => void,
) {
	const handleScroll = createScrollHandler(scrollTimeoutRef, updateActiveIndex);
	const timeoutRef = scrollTimeoutRef;

	container.addEventListener('scroll', handleScroll);
	updateActiveIndex();

	return () => {
		container.removeEventListener('scroll', handleScroll);
		if (timeoutRef.current) {
			window.clearTimeout(timeoutRef.current);
		}
	};
}

function createScrollHandlers(activeIndex: number, itemCount: number, scrollToItem: (index: number) => void) {
	return {
		handleScrollLeft: () => {
			if (activeIndex > 0) {
				scrollToItem(activeIndex - 1);
			}
		},
		handleScrollRight: () => {
			if (activeIndex < itemCount - 1) {
				scrollToItem(activeIndex + 1);
			}
		},
	};
}

function useOverflowDetection(containerRef: React.RefObject<HTMLDivElement | null>): boolean {
	const [needsScroll, setNeedsScroll] = useState(false);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return undefined;

		const checkOverflow = () => {
			const overflow = container.scrollWidth - container.clientWidth;
			setNeedsScroll(overflow > SCROLL_THRESHOLD);
		};

		checkOverflow();

		const resizeObserver = new ResizeObserver(checkOverflow);
		resizeObserver.observe(container);
		window.addEventListener('resize', checkOverflow);

		return () => {
			resizeObserver.disconnect();
			window.removeEventListener('resize', checkOverflow);
		};
	}, [containerRef]);

	return needsScroll;
}

function useScrollState(containerRef: React.RefObject<HTMLDivElement | null>) {
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	const updateScrollState = useCallback(() => {
		const container = containerRef.current;
		if (!container) return;

		setCanScrollLeft(container.scrollLeft > SCROLL_THRESHOLD);
		const maxScrollLeft = container.scrollWidth - container.clientWidth;
		setCanScrollRight(container.scrollLeft < maxScrollLeft - SCROLL_THRESHOLD);
	}, [containerRef]);

	useEffect(() => {
		window.addEventListener('resize', updateScrollState);
		return () => window.removeEventListener('resize', updateScrollState);
	}, [updateScrollState]);

	return { canScrollLeft, canScrollRight, updateScrollState };
}

function useInitialScroll(initialIndex: number, scrollToItem: (index: number, behavior?: ScrollBehavior) => void) {
	useLayoutEffect(() => {
		scrollToItem(initialIndex, 'auto');
	}, [initialIndex, scrollToItem]);
}

function useCarouselActiveIndex(refs: CarouselRefs, options: UseCarouselActiveIndexOptions) {
	const { scrollContainerRef, itemRefs } = refs;
	const { itemCount, initialIndex, loop, updateScrollState } = options;
	const [activeIndex, setActiveIndex] = useState(initialIndex);
	const scrollTimeoutRef = useRef<number | null>(null);

	const scrollToItem = useCallback(
		// eslint-disable-next-line react-hooks/preserve-manual-memoization
		(index: number, behavior: ScrollBehavior = 'smooth') => {
			const item = itemRefs.current[index];
			const container = scrollContainerRef.current;
			if (item && container) {
				const scrollLeft = Math.max(0, item.offsetLeft - (container.clientWidth - item.offsetWidth) / 2);
				container.scrollTo({ left: scrollLeft, behavior });
			}
		},
		[itemRefs, scrollContainerRef],
	);

	const updateActiveIndex = useCallback(() => {
		const container = scrollContainerRef.current;
		if (!container || container.clientWidth === 0) return;
		const closestIndex = findClosestItemIndex(container, itemRefs);

		if (loop) {
			if (closestIndex === 0) {
				const realLastIndex = itemCount - 2;
				scrollToItem(realLastIndex, 'auto');
				setActiveIndex(realLastIndex);
				return;
			}
			if (closestIndex === itemCount - 1) {
				scrollToItem(1, 'auto');
				setActiveIndex(1);
				return;
			}
		}

		setActiveIndex(closestIndex);
		updateScrollState();
	}, [loop, itemCount, scrollToItem, updateScrollState, scrollContainerRef, itemRefs]);

	useEffect(() => {
		const container = scrollContainerRef.current;
		if (!container) return undefined;
		updateScrollState();
		return setupScrollListener(container, scrollTimeoutRef, updateActiveIndex);
	}, [itemCount, updateActiveIndex, updateScrollState, scrollContainerRef]);

	useInitialScroll(initialIndex, scrollToItem);

	return { activeIndex, scrollToItem };
}

export function useCardCarousel({ itemCount, initialIndex = 0 }: UseCardCarouselProps): UseCardCarouselReturn {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
	const loop = itemCount > 1;

	const needsScroll = useOverflowDetection(scrollContainerRef);
	const { canScrollLeft, canScrollRight, updateScrollState } = useScrollState(scrollContainerRef);

	const { activeIndex, scrollToItem } = useCarouselActiveIndex(
		{ scrollContainerRef, itemRefs },
		{ itemCount, initialIndex, loop, updateScrollState },
	);

	const { handleScrollLeft, handleScrollRight } = useMemo(
		() => createScrollHandlers(activeIndex, itemCount, scrollToItem),
		[activeIndex, itemCount, scrollToItem],
	);

	const setItemRef = useCallback(
		(index: number) => (el: HTMLDivElement | null) => {
			itemRefs.current[index] = el;
		},
		[],
	);

	const exposedActiveIndex = useMemo(() => {
		if (!loop) return activeIndex;
		if (activeIndex === 0) return itemCount - 3;
		if (activeIndex === itemCount - 1) return 0;
		return activeIndex - 1;
	}, [activeIndex, loop, itemCount]);

	return {
		scrollContainerRef,
		itemRefs,
		activeIndex: exposedActiveIndex,
		canScrollLeft,
		canScrollRight,
		needsScroll,
		handleScrollLeft,
		handleScrollRight,
		setItemRef,
	};
}
